import * as React from "react";
import {
  AnyFullfillment,
  DownloadFulfillment,
  ReadExternalFulfillment,
  ReadInternalFulfillment
} from "utils/fulfill";
import { FulfillableBook } from "interfaces";
import track from "analytics/track";
import SvgDownload from "icons/Download";
import SvgExternalLink from "icons/ExternalOpen";
import { useRouter } from "next/router";
import { Text } from "components/Text";
import Button from "components/Button";
import useLibraryContext from "components/context/LibraryContext";
import useUser from "components/context/UserContext";
import downloadFile from "dataflow/download";
import useError from "hooks/useError";
import useLinkUtils from "hooks/useLinkUtils";
import { openPendingTab } from "utils/window";
import Stack from "./Stack";
import { useTranslation } from "next-i18next/pages";

const FulfillmentButton: React.FC<{
  details: AnyFullfillment;
  book: FulfillableBook;
  isPrimaryAction: boolean;
}> = ({ details, book, isPrimaryAction }) => {
  switch (details.type) {
    case "download":
      return (
        <DownloadButton
          details={details}
          title={book.title}
          isPrimaryAction={isPrimaryAction}
        />
      );
    case "read-online-internal":
      return (
        <ReadOnlineInternal
          details={details}
          isPrimaryAction={isPrimaryAction}
          trackOpenBookUrl={book.trackOpenBookUrl}
        />
      );
    case "read-online-external":
      return (
        <ReadOnlineExternal
          details={details}
          isPrimaryAction={isPrimaryAction}
          trackOpenBookUrl={book.trackOpenBookUrl}
        />
      );
    case "unsupported":
      return null;
  }
};

export default FulfillmentButton;

function getButtonStyles(isPrimaryAction: boolean) {
  return isPrimaryAction
    ? ({
        variant: "filled",
        color: "ui.black"
      } as const)
    : ({
        variant: "ghost",
        color: "ui.gray.extraDark"
      } as const);
}

const ReadOnlineExternal: React.FC<{
  details: ReadExternalFulfillment;
  isPrimaryAction: boolean;
  trackOpenBookUrl: string | null;
}> = ({ details, isPrimaryAction, trackOpenBookUrl }) => {
  const { t } = useTranslation();
  const { catalogUrl } = useLibraryContext();
  const { token } = useUser();
  const [loading, setLoading] = React.useState(false);
  const { error, handleError, clearError } = useError();

  async function open() {
    setLoading(true);
    clearError();
    const tab = openPendingTab();
    try {
      const { url: externalReaderUrl } = await details.getLocation(
        catalogUrl,
        token
      );
      track.openBook(trackOpenBookUrl);
      setLoading(false);
      tab.navigate(externalReaderUrl);
    } catch (e) {
      setLoading(false);
      tab.close();
      handleError(e);
    }
  }

  return (
    <Stack sx={{ flexWrap: "wrap" }}>
      <Button
        {...getButtonStyles(isPrimaryAction)}
        iconLeft={SvgExternalLink}
        onClick={open}
        loading={loading}
        loadingText={t("fulfillmentButton.opening", "Opening...")}
      >
        {details?.buttonLabel ?? t("fulfillmentButton.read", "Read")}
      </Button>
      {error && <Text sx={{ color: "ui.error" }}>{error}</Text>}
    </Stack>
  );
};

const ReadOnlineInternal: React.FC<{
  details: ReadInternalFulfillment;
  trackOpenBookUrl: string | null;
  isPrimaryAction: boolean;
}> = ({ details, isPrimaryAction, trackOpenBookUrl }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { buildReaderLink } = useLinkUtils();

  const internalLink = buildReaderLink("internal", details.url);
  function open() {
    track.openBook(trackOpenBookUrl);
    router.push(internalLink, undefined, { shallow: true });
  }
  return (
    <Button {...getButtonStyles(isPrimaryAction)} onClick={open}>
      {details?.buttonLabel ?? t("fulfillmentButton.read", "Read")}
    </Button>
  );
};

const DownloadButton: React.FC<{
  details: DownloadFulfillment;
  title: string;
  isPrimaryAction: boolean;
}> = ({ details, title, isPrimaryAction }) => {
  const { t } = useTranslation();
  const { buttonLabel } = details;
  const [loading, setLoading] = React.useState(false);
  const { error, handleError, clearError } = useError();
  const { catalogUrl } = useLibraryContext();
  const { token } = useUser();

  async function download() {
    setLoading(true);
    clearError();
    try {
      const { url: downloadUrl, token: downloadToken } =
        await details.getLocation(catalogUrl, token);

      await downloadFile(
        downloadUrl,
        title,
        details.contentType,
        downloadToken
      );
    } catch (e) {
      setLoading(false);
      handleError(e);
    }
    setLoading(false);
  }

  return (
    <>
      <Button
        onClick={download}
        {...getButtonStyles(isPrimaryAction)}
        iconLeft={SvgDownload}
        loading={loading}
        loadingText={t("fulfillmentButton.downloading", "Downloading...")}
      >
        {buttonLabel}
      </Button>
      {error && <Text sx={{ color: "ui.error" }}>{error}</Text>}
    </>
  );
};
